import { colors, font, fontSizing, spacing } from "../theme";
import styled, { css } from "styled-components";
import * as React from "react";
import {
  pushRouteWithQuery,
  RouteName,
  RouteParams,
  Router
} from "../../lib/routes";
import Link from "../utils/Link";
import { ArrowRight } from "../common/icons";
import Content from "../common/Content";
import Grid from "../common/Grid";
import { ApiResult } from "../../lib/CmsApi";
import A from "../common/A";
import TopBox from "../common/TopBox";
import Flex from "../common/Flex";

const Container = styled.div`
  @media print {
    display: none;
  }
`;

const FlexSteps = styled(Flex)`
  border-top: 2px solid ${colors.pigeonSuperLight};
  border-bottom: 2px solid ${colors.pigeonSuperLight};
  ${spacing("padding-top", 12)}
  ${spacing("padding-bottom", 12)}

  li {
    list-style: none;
  }
`;
const SubLinks = styled(A)<{ active?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${colors.blue};
  ${fontSizing("label", 32)}
  font-weight:${font.weightRegular};
  > span {
    margin-right: 1ch;
  }
  > svg {
    transition: transform 0.3s ease-in-out;
    height:0.5em;
    width:0.5em;
  }
  ${p =>
    p.active &&
    css`
      font-weight: ${font.weightMedium};
      color: ${colors.bluegreyDarker};
      > svg {
        transform: rotate(90deg);
      }
    `}
`;
const Tag = styled.button<{ active?: boolean }>`
  border: none;
  border-radius: 4px;
  ${spacing("padding-top", 8)}
  ${spacing("padding-bottom", 8)}
  ${spacing("padding-left", 16)}
  ${spacing("padding-right", 16)}
  ${spacing("margin", 12)}
  ${fontSizing("label", 20)}
  cursor: pointer;
  ${p =>
    p.active
      ? css`
          background-color: ${colors.calypso};
          color: #fff;
        `
      : css`
          background-color: ${colors.paleGreyTwo};
          color: ${colors.blue};
        `}
`;
const StyledGrid = styled(Grid)`
  width: 100%;
  height: 100%;
  > div {
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;
interface Props {
  breadCrumpPath?: { route: RouteName; params?: RouteParams; title: string }[];
  activeTab?: string;
  activeTags: string[];
  tags: ApiResult<"listTags">;
}
const MagazinHeader = (props: Props) => {
  const onClickTag = (id: string) => {
    let tags =
      (Router.query.tags && (Router.query.tags as string).split(",")) || [];
    if (tags.includes(id)) {
      tags = tags.filter(t => t !== id);
    } else {
      tags.push(id);
    }
    const query = {
      ...Router.query,
      page: undefined,
      tags: tags.join(",")
    };
    pushRouteWithQuery(Router.route.replace("/", "") as RouteName, query);
  };
  const linkParams = { tags: props.activeTags.join(",") };
  return (
    <Container>
      <TopBox
        title="Magazin"
        breadCrumpPath={[
          { route: "magazin", title: "Magazin" },
          ...(props.breadCrumpPath ? props.breadCrumpPath : [])
        ]}
        fullInfoBox
        infoBox={
          <StyledGrid
            columns={3}
            tinyColumns={1}
            as="nav"
            aria-label="Magazin Kategorien"
          >
            <div>
              <Link route={"news"} params={linkParams}>
                <SubLinks
                  active={props.activeTab === "news"}
                  tabIndex={0}
                  aria-current={
                    props.activeTab === "news" ? "location" : undefined
                  }
                >
                  <span>Aktuelles</span>
                  <ArrowRight />
                </SubLinks>
              </Link>
            </div>
            <div>
              <Link route={"portraits"} params={linkParams}>
                <SubLinks
                  active={props.activeTab === "portraits"}
                  tabIndex={0}
                  aria-current={
                    props.activeTab === "portraits" ? "location" : undefined
                  }
                >
                  <span>Förderportraits</span>
                  <ArrowRight />
                </SubLinks>
              </Link>
            </div>
            <div>
              <Link route={"events"} params={linkParams}>
                <SubLinks
                  active={props.activeTab === "events"}
                  tabIndex={0}
                  aria-current={
                    props.activeTab === "events" ? "location" : undefined
                  }
                >
                  <span>Veranstaltungen</span>
                  <ArrowRight />
                </SubLinks>
              </Link>
            </div>
          </StyledGrid>
        }
      />
      <Content
        relative
        padded
        smallBottomMargin
        as="nav"
        aria-label="Schlagwortfilter"
      >
        <FlexSteps
          as={"ul"}
          alignItems="center"
          justifyContent="center"
          flexWrap
          role="listbox"
        >
          {props.tags.items.map(tag => {
            const isActive = props.activeTags.includes(tag._id);
            return (
              <li key={tag._id}>
                <Tag
                  aria-selected={isActive ? "true" : "false"}
                  active={isActive}
                  onClick={() => onClickTag(tag._id)}
                  role="option"
                >
                  {tag.name}
                </Tag>
              </li>
            );
          })}
        </FlexSteps>
      </Content>
    </Container>
  );
};

export default MagazinHeader;
